import { Component, computed, OnDestroy, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { interval, lastValueFrom, Subscription } from 'rxjs';
import { LoadService } from 'src/app/services/application/load.service';
import { AuthService } from 'src/app/services/auth.service';
import { Utils } from 'src/app/services/utils';

@Component({
    selector: 'app-auth-codes',
    templateUrl: './codes.component.html',
    styleUrls: ['../_auth-shared-styles.scss'],
    standalone: false,
})
export class AuthCodesComponent implements OnInit, OnDestroy {
    public authData = this.auth.authCodeDataReadOnly;
    public authAction = this.auth.authActionReadonly;
    public AuthFormTitle: string = '';
    public AccessReg: boolean = false;
    public errorMessage = this.auth.authErrorReadOnly;
    public otpCode: string = '';
    public hasAuthCodeContext: boolean = false;
    public loadingCheckCode = this.auth.loadingReadOnly;
    public loadingResendCode: boolean = false;
    private timerSeconds = signal<number>(120);
    public timerDuration: number = 120; // здесь устанавливаем таймер на нужное время в секундах
    private isRunning = signal<boolean>(false);
    public isTimerRunning = computed(() => this.isRunning());
    private subscription: Subscription | null = null;

    public constructor(
        private router: Router,
        private auth: AuthService,
        private load: LoadService,
    ) {
        this.AuthFormTitle =
            this.load.configData?.CLIENT_SETTINGS.PAGES_SETTINGS.LOGIN
                .AUTH_FORM_TEXT ?? 'Личный кабинет пациента';
        this.AccessReg =
            !!this.load.configData?.CLIENT_SETTINGS.PAGES_SETTINGS.LOGIN
                .IS_REGISTRATION_ENABLED;
    }

    public ngOnInit(): void {
        this.hasAuthCodeContext = this.isAuthCodeContextValid();
        if (!this.hasAuthCodeContext) {
            void this.router.navigate(['/auth/login'], { replaceUrl: true });
            return;
        }

        this.startTimer(this.timerDuration);
    }
    public ngOnDestroy(): void {
        this.stopTimer();
    }
    public onOtpChange(code: string) {
        this.otpCode = code;
    }
    // срабатывает когда введен код подтверждения
    public onOtpComplete(code: string) {
        this.auth.submitAuthCode(code);
    }
    // переотправка кода
    public async resendAuthCode() {
        this.stopTimer();
        this.loadingResendCode = true;
        try {
            const response = await lastValueFrom(
                this.auth.resendAuthenticationCode(
                    this.authData().messageUuid,
                    this.authData().delieveryChanel,
                ),
            );
            if (response.messageUuid) {
                this.auth.updateMessageUuid(response.messageUuid);
                this.otpCode = '';
                this.startTimer(this.timerDuration);
            }
        } catch (err: unknown) {
            this.auth.updateAuthError(
                Utils.getErrorMessage(err, 'Ошибка при проверке кода'),
            );
        } finally {
            this.loadingResendCode = false;
        }
    }
    public goBack() {
        switch (this.authAction()) {
            case 'LOGIN':
                this.router.navigate(['/auth/login']);
                break;
            case 'RECOVERY':
                this.router.navigate(['/auth/recovery']);
                break;
            default:
                this.router.navigate(['/auth/login']);
                break;
        }
    }
    public formattedPhoneOrMail() {
        const phone = this.authData().phoneOrMail;
        if (phone.includes('@')) return phone;
        return `+${phone.slice(0, 1)} ${phone.slice(1, 4)}-${phone.slice(4, 7)}-${phone.slice(7, 9)}-${phone.slice(9)}`;
    }
    // отображение таймера
    public timerDisplay = computed(() => {
        const seconds = this.timerSeconds();
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    });

    private startTimer(seconds: number): void {
        // Останавливаем предыдущий таймер, если есть
        this.stopTimer();

        // Устанавливаем начальное значение
        this.timerSeconds.set(seconds);
        this.isRunning.set(true);

        // Запускаем интервал
        this.subscription = interval(1000).subscribe(() => {
            const currentValue = this.timerSeconds();
            const newValue = currentValue - 1;

            if (newValue <= 0) {
                // Таймер закончился
                this.timerSeconds.set(0);
                this.isRunning.set(false);
                this.stopTimer();
            } else {
                this.timerSeconds.set(newValue);
            }
        });
    }

    // Метод для остановки таймера
    private stopTimer(): void {
        this.isRunning.set(false);
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = null;
        }
    }

    private isAuthCodeContextValid(): boolean {
        const data = this.authData();
        const action = this.authAction();

        return (
            data.messageUuid.trim().length > 0 &&
            data.phoneOrMail.trim().length > 0 &&
            (data.delieveryChanel === 'EMAIL' ||
                data.delieveryChanel === 'SMS') &&
            (action === 'LOGIN' || action === 'RECOVERY')
        );
    }
}
